import { t } from 'i18next';
import { LitElement } from 'lit';

import {
  ConfigurationPrintOptionPersonalizationDto,
  PrintOptionCollapsibleDto,
  PrintOptionCollapsibleStepDto,
  getShowConfigurationPersonalizations,
} from '@/api-sdk';
import {
  PersonalisationType,
  personalisation as personalisationContext,
} from '@/context';

/**
 * Fetches and processes personalisation data for a given element and UUID.
 *
 * This function retrieves personalisation data from the ConfigurationService
 * using the provided UUID. It then processes the data and triggers an update
 * event on the given element with the personalisation details.
 *
 * @param element - The LitElement instance to update with personalisation data.
 * @param uuid - The UUID used to fetch the personalisation data.
 * @returns A promise that resolves to the personalisation data or undefined if no data is found.
 */
export const getPersonalisationData = async (
  element: LitElement,
  uuid: string,
): Promise<PersonalisationType | undefined> => {
  const ConfigurationPersonalizationsPromise =
    getShowConfigurationPersonalizations({
      uuid: uuid,
    });

  const personalisation = await ConfigurationPersonalizationsPromise,
    personalisationData: ConfigurationPrintOptionPersonalizationDto[] =
      personalisation;

  if (!personalisationData) {
    return undefined;
  }

  personalisationData.map(personalisation => {
    const personalisationData = personalisation.personalization;

    personalisationContext.providerUpdateEvent(element, {
      printOptionCode: personalisation?.printOptionCode,
      type: personalisationData?.type,
      textPersonalization: personalisationData?.textPersonalization,
      uploadedFileName: personalisationData?.logo,
      colors: personalisationData?.colors,
      logoUuid: personalisationData?.logoUuid,
      visualDesignId: personalisationData?.visualDesignId,
    });
  });
};

export const getPrintOptionDisplayData = (
  stepData: PrintOptionCollapsibleStepDto,
  printOption: PrintOptionCollapsibleDto,
): {
  description: {
    selectedOption: string | boolean;
    additionalSelectedOption: string | boolean;
  };
  a11yTitle: string;
  subtitle: string;
} => {
  const description = stepData.unprinted
    ? {
        selectedOption: t('actions.noPrint'),
        additionalSelectedOption: false,
      }
    : {
        selectedOption: printOption.selectedPrintOption?.position || false,
        additionalSelectedOption:
          printOption.selectedPrintOption?.maxSurface || false,
      };

  const a11yTitle = [
    printOption.title,
    printOption?.selectedPrintOption?.position,
    printOption?.selectedPrintOption?.maxSurface,
  ]
    .filter(Boolean)
    .join(' ');

  const subtitle = [
    printOption?.selectedPrintOption?.printTechnique,
    printOption?.selectedPrintOption?.colorCount != null
      ? t('content.configuration.colorCount', {
          count: printOption?.selectedPrintOption?.colorCount,
        })
      : printOption?.selectedPrintOption?.colorCount === null
        ? t('labels.fullColor')
        : null,
  ]
    .filter(Boolean)
    .join(', ');

  return { description, a11yTitle, subtitle };
};
